"use client"

import React, { useMemo, useState, useEffect, memo } from "react"
import { useParams, usePathname } from "next/navigation"
import { Message } from "@locmod/intl"
import { useLive, useNavigation } from "@azuro-org/sdk"
import { type NavigationQuery } from "@azuro-org/toolkit"
import cx from "classnames"
import { constants } from "helpers"

import { Icon, type IconName } from "components/ui"
import { Href } from "components/navigation"
import { Flag } from "components/dataDisplay"

import Skeleton from "./components/Skeleton/Skeleton"

import messages from "./messages"
import { useOvertimeFormattedSportsMarkets } from "@/hooks/useSportsMarkets"
import { useIsMounted } from "@/hooks"
import { transformToNavigationData } from "@/Overtime/utils/transformNavigationData"
import { useSxHomeMarkets, useSxSportsNavigation } from "@/SX/hooks"
import useChainProviderHook from "@/hooks/useChainProvider"
import type { SxNavigationData } from "@/SX/types/markets"

/**
 * Props for the League component
 */
type LeagueProps =
  NavigationQuery["sports"][0]["countries"][0]["leagues"][0] & {
    url: string
    country: {
      name: string
      slug: string
    }
  }

/**
 * League component displays individual league items within a sport
 * Shows country flag, league name, and active games count
 */
const League: React.FC<LeagueProps> = props => {
  const {
    url,
    name,
    country,
    slug,
    activePrematchGamesCount,
    activeLiveGamesCount,
  } = props

  const { isLive } = useLive()
  const { isSXChain } = useChainProviderHook()

  const { countrySlug, leagueSlug } = useParams()!
  const gamesCount = isLive ? activeLiveGamesCount : activePrematchGamesCount

  const isActive =
    Boolean(leagueSlug) && countrySlug === country.slug && slug === leagueSlug

  const rootClassName = cx(
    "flex items-center justify-between py-2 px-4 hover:text-accent-grey-30",
    {
      "text-accent-grey-200": !isActive,
      "text-accent-grey-30": isActive,
    }
  )

  return (
    <Href to={url} className={rootClassName}>
      <div className="flex items-center overflow-hidden">
        <Flag className="mr-2 flex-none" country={country.slug} />
        <div className="text-caption-13 text-ellipsis whitespace-nowrap overflow-hidden">
          {name}
        </div>
      </div>
      {!isSXChain && (
        <div className="bg-accent-grey-1000 px-2 py-px ml-2 text-caption-12 rounded-full border border-accent-grey-950 min-w-[28px] text-center">
          {gamesCount}
        </div>
      )}
    </Href>
  )
}

/**
 * Top section type for the main navigation
 */
type Top = {
  slug: "/"
  name: Intl.Message
  gamesCount?: number
}

/**
 * Props for the Sport component
 */
type SportProps = {
  gamesCount?: number
  expandedSport: string | null
  onSportToggle: (sportSlug: string) => void
} & (Top | NavigationQuery["sports"][0])

/**
 * Sport component displays individual sport items with expandable leagues
 * Handles navigation to sport pages and expand/collapse functionality
 */
const Sport: React.FC<SportProps> = props => {
  const { isSXChain } = useChainProviderHook()
  const { slug, name, countries, expandedSport, onSportToggle } =
    props as NavigationQuery["sports"][0] & {
      expandedSport: string | null
      onSportToggle: (sportSlug: string) => void
    }
  const { gamesCount } = props as Top
  const isTop = slug === "/"

  const isSportExpanded = expandedSport === slug
  const isFullyActive = isSportExpanded

  const isUnique = slug === "unique"

  const rootClassName = cx("p-px rounded-sm", {
    "bg-card-border-top": isFullyActive,
  })
  const wrapperClassName = cx({
    "bg-accent-grey-900 rounded-sm border top-gradient-border": isFullyActive,
  })
  const buttonClassName = cx(
    "group px-4 py-2.5 flex w-full items-center font-semibold justify-between hover:text-brand-light-500",
    {
      "text-accent-grey-200": !isFullyActive,
      "text-brand-light-500": isFullyActive && !isTop,
      "text-accent-grey-20": isFullyActive && isTop,
    }
  )
  const iconClassName = cx("h-4 w-4", {
    "rotate-180 text-brand-light-500": isFullyActive,
  })

  // Special icon mapping for SX.bet sports
  const getIconName = (): IconName => {
    if (isTop || isUnique) {
      return "interface/top"
    }

    if (isSXChain) {
      switch (slug) {
        case "racing":
          return "sport/motosport"
        case "mixed-martial-arts":
          return "sport/boxing"
        case "degen-crypto":
          return "sport/crypto"
        default:
          return `sport/${slug}` as IconName
      }
    }

    return `sport/${slug}` as IconName
  }

  const icon = getIconName()

  /**
   * Memoized leagues data with URL generation
   */
  const leagues = useMemo(() => {
    if (!countries) {
      return
    }

    return countries
      .map(({ leagues, name, slug: countrySlug }) => {
        return leagues.map(league => ({
          url: `/${slug}/${countrySlug}/${league.slug}`,
          ...league,
          country: {
            name,
            slug: countrySlug,
          },
        }))
      })
      .flat()
  }, [countries])

  /**
   * Handles sport expand/collapse toggle
   */
  const handleSportToggle = () => {
    onSportToggle(slug)
  }

  return (
    <div className={rootClassName}>
      <div className={wrapperClassName}>
        <div className={buttonClassName}>
          <Href to={`/${slug}`}>
            <div className="flex items-center">
              <Icon
                className={`size-4 mr-2  ${isFullyActive && "text-brand-light-500"}`}
                name={icon}
              />
              <Message className="text-caption-13 " value={name} />
            </div>
          </Href>
          {Boolean(isTop || isUnique || !leagues?.length) ? (
            <div className="text-caption-12 min-w-4 text-center">
              {gamesCount || 0}
            </div>
          ) : (
            <div
              onClick={e => {
                e.stopPropagation()
                handleSportToggle()
              }}
              className="cursor-pointer"
            >
              <Icon className={iconClassName} name="interface/chevron_down" />
            </div>
          )}
        </div>
        {Boolean(!isUnique && isFullyActive && leagues) &&
          leagues?.map(league => (
            <League key={`${league.country.slug}-${league.slug}`} {...league} />
          ))}
      </div>
    </div>
  )
}

/**
 * Props for the Navigation component
 */
type NavigationProps = {
  /** Optional CSS class name for styling */
  className?: string
}

/**
 * Navigation component displays the main sports navigation sidebar
 *
 * Features:
 * - Displays all available sports with game counts
 * - Supports expandable leagues under each sport
 * - Automatically collapses expanded sports on route changes
 * - Shows live/prematch game counts based on current view
 * - Handles navigation to sport and league pages
 *
 * @param props - Navigation component props
 * @returns Navigation component JSX
 */
const Navigation: React.FC<NavigationProps> = memo(({ className }) => {
  const { isLive } = useLive()
  const isMounted = useIsMounted()

  const { isOvertimeChain, isAzuroChain, isSXChain } = useChainProviderHook()

  const { data: sdkNavigation, isFetching: isSdkFetching } = useNavigation({
    isLive,
    query: {
      enabled: isAzuroChain,
    },
  })

  const {
    openSportMarkets: overtimeNavigation,
    isLoading: isOvertimeFetching,
  } = useOvertimeFormattedSportsMarkets()

  // Memoize raw overtime data to prevent unnecessary re-processing
  const memoizedOvertimeData = useMemo(() => {
    return overtimeNavigation
  }, [overtimeNavigation])

  const {
    data: { navigation: sxNavigationFromMarkets } = {},
    isLoading: isSxFetchingFromMarkets,
  } = useSxSportsNavigation()

  // Get market length from streaming markets
  const { marketsCount } = useSxHomeMarkets()

  const { sports: sxNavigation } = (sxNavigationFromMarkets ||
    {}) as SxNavigationData["navigation"]

  // Use markets.length for prematch count
  const pathname = usePathname()

  // Global state to track which sport is expanded
  const [expandedSport, setExpandedSport] = useState<string | null>(null)

  /**
   * Closes expanded sport when route changes
   * This ensures navigation state stays clean during navigation
   */
  useEffect(() => {
    setExpandedSport(null)
  }, [pathname])

  /**
   * Handles sport expand/collapse toggle
   * @param sportSlug - The slug of the sport to toggle
   */
  const handleSportToggle = (sportSlug: string) => {
    setExpandedSport(expandedSport === sportSlug ? null : sportSlug)
  }

  /**
   * Get navigation data based on chain type
   */
  const navigationData = useMemo(() => {
    switch (true) {
      case isOvertimeChain:
        return transformToNavigationData(
          memoizedOvertimeData,
          isLive
        ) as unknown as NavigationQuery["sports"]
      case isSXChain:
        return sxNavigation
      case isAzuroChain:
      default:
        return sdkNavigation
    }
  }, [
    isOvertimeChain,
    isSXChain,
    isAzuroChain,
    memoizedOvertimeData,
    isLive,
    sxNavigation,
    sdkNavigation,
  ])

  /**
   * Determine if we're still fetching data based on chain type
   */
  const isFetchingData = useMemo(() => {
    switch (true) {
      case isOvertimeChain:
        return isOvertimeFetching
      case isSXChain:
        return isSxFetchingFromMarkets
      case isAzuroChain:
      default:
        return isSdkFetching
    }
  }, [
    isOvertimeChain,
    isSXChain,
    isAzuroChain,
    isOvertimeFetching,
    isSxFetchingFromMarkets,
    isSdkFetching,
  ])

  /**
   * Calculate if we should show skeleton:
   * - If we're fetching data AND we don't have navigation data yet
   * - OR if we're not mounted yet
   */
  const shouldShowSkeleton = !isMounted || (isFetchingData && !navigationData)

  /**
   * Calculates total games count for the "Top" section
   * Limits games per sport based on constants.topPageGamePerSportLimit
   */
  const allTopGames = useMemo(() => {
    if (!navigationData) {
      return 0
    }

    if (isSXChain) {
      return marketsCount
    }

    return (
      navigationData as Array<{
        activeLiveGamesCount: number
        activePrematchGamesCount: number
      }>
    ).reduce((acc, { activeLiveGamesCount, activePrematchGamesCount }) => {
      const gamesCount = isLive
        ? activeLiveGamesCount
        : activePrematchGamesCount
      return (
        acc +
        Math.min(
          gamesCount,
          isOvertimeChain
            ? constants.topPageGamePerSportLimitOvertime
            : constants.topPageGamePerSportLimit
        )
      )
    }, 0)
  }, [navigationData, isLive, isSXChain, marketsCount, isOvertimeChain])

  /**
   * Sorts sports based on predefined order in constants.sportsOrder
   * Sports without leagues are moved to the end
   */
  const sortedSports = useMemo(() => {
    if (!navigationData) {
      return []
    }

    return [...(navigationData as NavigationQuery["sports"])].sort(
      (sport1, sport2) => {
        const sport1Index = constants.sportsOrder.indexOf(sport1.slug)
        const sport2Index = constants.sportsOrder.indexOf(sport2.slug)

        if (!sport1.countries.length || !sport1.countries[0].leagues.length) {
          return 1
        }

        if (!sport2.countries.length || !sport2.countries[0].leagues.length) {
          return -1
        }

        if (sport1Index >= 0 && sport2Index >= 0) {
          return sport1Index - sport2Index
        }

        if (sport1Index < 0 && sport2Index >= 0) {
          return 1
        }

        if (sport1Index >= 0 && sport2Index < 0) {
          return -1
        }

        return 0
      }
    )
  }, [navigationData])

  // Show skeleton while loading
  if (shouldShowSkeleton) {
    return <Skeleton className={className} />
  }

  return (
    <div className={className}>
      <Message
        className="text-caption-13 font-semibold py-2 px-4 mb-2"
        value={messages.title}
        tag="p"
      />
      <Sport
        slug="/"
        name={messages.top}
        gamesCount={allTopGames}
        expandedSport={expandedSport}
        onSportToggle={handleSportToggle}
      />
      {sortedSports?.map(sport => {
        const { activeLiveGamesCount, activePrematchGamesCount } = sport
        const gamesCount = isLive
          ? activeLiveGamesCount
          : activePrematchGamesCount

        return (
          <Sport
            key={sport.slug}
            gamesCount={gamesCount}
            {...sport}
            expandedSport={expandedSport}
            onSportToggle={handleSportToggle}
          />
        )
      })}
    </div>
  )
})

Navigation.displayName = "Navigation"

export default Navigation
